/**
 * src/game/progression/commander.ts — 玩遊戲軌(指揮官等級)single source of truth.
 *
 * Spec: docs/spec/PROGRESSION_AND_ACCOUNTS.md §3.2 (玩遊戲軌) + §3.3 (資料模型).
 *
 * 雙軌進度鐵則 (CLAUDE.md 最高優先 / spec §5 / §7 紅線):
 *   - 學習 = 戰力深度:只有 factionXP 能解鎖技能 / 提升 skillLevels / 影響戰鬥計算。
 *   - 玩遊戲 = 帳號廣度:擊殺/波次/魔王/通關 → commanderXp → 只解鎖
 *     英雄/造型/關卡/模式 + 封頂 QoL(起手金幣 ≤100、重抽 ≤2)。
 *   - 本模組 **NEVER** 寫入 factionXP / freeXP / skillLevels / 傷害 / HP / 冷卻。
 *     它只動 commanderXp / commanderLevel / unlockedHeroes / unlockedStages /
 *     unlockedModes / unlockedCommanderCosmetics / qol。
 *
 * 對齊 src/learn/scoring/xp.ts 的 thresholds-array + 反向 walk pattern,但**不污染**
 * 學習端 xp.ts(物理隔離,避免兩軌漂移)。mutate-in-place 風格對齊
 * shared/progress.ts:addFactionXP — caller 負責 saveProgress(p)。
 */
import type { FactionId, PlayerProgress } from '../../shared/types';

/**
 * Default 主人翁 / starter hero. Mirrors `DEFAULT_HERO_ID` in shared/progress.ts
 * but redeclared locally to avoid a module-eval circular import (progress.ts
 * imports computeCommanderLevel from this file for its v28→29 migration).
 * 'celestial' (星算門) is always default-unlocked — never a locked starter.
 */
const DEFAULT_HERO_ID: FactionId = 'celestial';

// ── 指揮官等級曲線(1..30)─────────────────────────────────────────────────
//
// spec §3.2.2:累進、前低後高 — 前 10 級快速給成就感,後段拉長黏著。
// Index = level - 1;值 = 達到該級所需累計 commanderXp。
// 前段刻意低(打幾場就升),配合英雄在 Lv2~Lv5 解鎖(task:門檻寬鬆)。
export const COMMANDER_LEVEL_THRESHOLDS: readonly number[] = [
  0,       // Lv1
  200,     // Lv2
  500,     // Lv3
  1000,    // Lv4
  1800,    // Lv5
  3000,    // Lv6
  4600,    // Lv7
  6800,    // Lv8
  9600,    // Lv9
  13000,   // Lv10  ← 首個「資深指揮官」里程碑
  17500,   // Lv11
  23000,   // Lv12
  29500,   // Lv13
  37000,   // Lv14
  45500,   // Lv15
  55000,   // Lv16
  65500,   // Lv17
  77000,   // Lv18
  89500,   // Lv19
  103000,  // Lv20
  118000,  // Lv21
  134000,  // Lv22
  151000,  // Lv23
  169000,  // Lv24
  188000,  // Lv25
  192000,  // Lv26 (尾段壓平,逼近封頂)
  195000,  // Lv27
  197000,  // Lv28
  199000,  // Lv29
  200000,  // Lv30  ← 封頂(K-4 再開)
];

export const MAX_COMMANDER_LEVEL = COMMANDER_LEVEL_THRESHOLDS.length; // 30

// ── 解鎖表 ───────────────────────────────────────────────────────────────
//
// 英雄門檻設**寬鬆**(task 要求):celestial 預設已解鎖;其餘 4 英雄在
// Lv2~Lv5 就解鎖(打幾場就到),不鎖很久。QoL 封頂(起手金幣 ≤100、重抽 ≤2)。
// spec §3.2.3 的 stage/cosmetic/mode 解鎖也一併編入,但前段以英雄為主。

/** 英雄解鎖門檻(level → heroId)。celestial 為預設 starter,不列(永遠已解鎖)。 */
export const HERO_UNLOCK_LEVEL: Readonly<Record<Exclude<FactionId, 'celestial'>, number>> = {
  techno: 2,     // Lv2 機械斥
  runic: 3,      // Lv3 符文守
  elemental: 4,  // Lv4 自然御
  dominion: 5,   // Lv5 疆域王
};

/** QoL 硬性上限(spec §3.2.4)。只影響「便利」不影響戰力。 */
export const QOL_CAPS = {
  /** 起手金幣封頂 +100。 */
  startingCoins: 100,
  /** roguelite 三選一重抽封頂 +2 次/場。 */
  rerollCharges: 2,
} as const;

/** QoL 解鎖門檻 + 每級增量(封頂後不再加)。 */
const QOL_STARTING_COINS_AT_LEVEL = 10; // Lv10 起手金幣 +50(封頂 100)
const QOL_STARTING_COINS_GRANT = 50;
const QOL_REROLL_AT_LEVEL = 12;         // Lv12 重抽 +1(封頂 2)
const QOL_REROLL_GRANT = 1;

/** 純外觀造型解鎖(level → cosmetic id)。 */
export const COSMETIC_UNLOCK_LEVEL: Readonly<Record<string, number>> = {
  'tank-gold': 5,
  'tank-rainbow': 15,
};

/** 模式解鎖(level → mode id)。 */
export const MODE_UNLOCK_LEVEL: Readonly<Record<string, number>> = {
  'infinite-tower': 5,
  'extreme': 15,
};

/** 額外關卡解鎖(level → 最高 stage 編號)。預設 [1..5];Lv8 開到 6。 */
export const STAGE_UNLOCK_LEVEL: Readonly<Record<number, number>> = {
  6: 8,   // Lv8 解鎖 Stage 6
  7: 16,  // Lv16 解鎖 Stage 7
};

// ── commanderXp 公式(spec §3.2.1)─────────────────────────────────────────

/** 戰鬥結算時可用的 stats(對齊 result-scene BattleStats payload)。 */
export interface CommanderBattleStats {
  /** 清掉的波次數。 */
  wave: number;
  /** 擊殺數。 */
  killed: number;
  /** 撐住秒數(目前公式不計入,保留供未來調整)。 */
  survivedSec?: number;
  /** 局內分數(目前公式不計入)。 */
  score?: number;
}

/**
 * commanderXp 公式(spec §3.2.1)。純由戰鬥表現決定,**不乘任何學習係數**。
 *   wave*10 + killed*1 + (boss?100:0) + (stageCleared?50:0)
 * 一場普通勝利 ~150-250,boss 場 ~400+。
 */
export function computeCommanderXp(
  stats: CommanderBattleStats | undefined,
  bossDefeated: boolean,
  stageCleared: boolean,
): number {
  const wave = stats && Number.isFinite(stats.wave) && stats.wave > 0 ? Math.floor(stats.wave) : 0;
  const killed = stats && Number.isFinite(stats.killed) && stats.killed > 0 ? Math.floor(stats.killed) : 0;
  return wave * 10 + killed * 1 + (bossDefeated ? 100 : 0) + (stageCleared ? 50 : 0);
}

// ── 等級換算 ─────────────────────────────────────────────────────────────

/**
 * 把累計 commanderXp 換成指揮官等級(1..MAX_COMMANDER_LEVEL)。
 * 反向 walk:第一個 xp >= threshold 的 level 勝。
 * 負/非有限值 → Lv1。對齊 xp.ts:computeSubjectLevel pattern。
 */
export function computeCommanderLevel(xp: number): number {
  if (!Number.isFinite(xp) || xp <= 0) return 1;
  for (let lv = COMMANDER_LEVEL_THRESHOLDS.length; lv >= 1; lv--) {
    if (xp >= COMMANDER_LEVEL_THRESHOLDS[lv - 1]) return lv;
  }
  return 1;
}

/** 升下一級還需多少 commanderXp;已封頂回 0。 */
export function commanderXpToNextLevel(xp: number): number {
  const lv = computeCommanderLevel(xp);
  if (lv >= MAX_COMMANDER_LEVEL) return 0;
  return COMMANDER_LEVEL_THRESHOLDS[lv] - xp;
}

// ── 英雄解鎖 helpers ─────────────────────────────────────────────────────

/** 確保 unlockedHeroes 存在且至少含 starter + 當前 selectedHeroId。 */
function ensureHeroList(p: PlayerProgress): FactionId[] {
  if (!Array.isArray(p.unlockedHeroes)) {
    const set = new Set<FactionId>([DEFAULT_HERO_ID]);
    if (p.selectedHeroId) set.add(p.selectedHeroId);
    p.unlockedHeroes = [...set];
  }
  return p.unlockedHeroes;
}

/** 英雄是否已解鎖。celestial(starter)永遠 true。 */
export function isHeroUnlocked(p: PlayerProgress, heroId: FactionId): boolean {
  if (heroId === DEFAULT_HERO_ID) return true;
  return ensureHeroList(p).includes(heroId);
}

/** 冪等解鎖一個英雄(回傳是否「新」解鎖)。 */
export function unlockHero(p: PlayerProgress, heroId: FactionId): boolean {
  const list = ensureHeroList(p);
  if (list.includes(heroId)) return false;
  list.push(heroId);
  return true;
}

/** 給定等級「應該」已解鎖的全部英雄 id(含 starter)。 */
export function heroesUnlockedAtLevel(level: number): FactionId[] {
  const out: FactionId[] = [DEFAULT_HERO_ID];
  for (const [hero, lv] of Object.entries(HERO_UNLOCK_LEVEL) as [Exclude<FactionId, 'celestial'>, number][]) {
    if (level >= lv) out.push(hero);
  }
  return out;
}

// ── 起手金幣(QoL)─────────────────────────────────────────────────────────

/** 給定指揮官等級對應的起手金幣(封頂 QOL_CAPS.startingCoins)。 */
export function startingCoinsFor(level: number): number {
  if (!Number.isFinite(level) || level < QOL_STARTING_COINS_AT_LEVEL) return 0;
  return Math.min(QOL_STARTING_COINS_GRANT, QOL_CAPS.startingCoins);
}

// ── 解鎖結果 ─────────────────────────────────────────────────────────────

export interface CommanderUnlocks {
  heroes: FactionId[];
  modes: string[];
  cosmetics: string[];
  stages: number[];
  /** QoL 欄位是否在這次升級被觸發(供 UI 文案)。 */
  qol: string[];
}

export interface AddCommanderXpResult {
  /** 實際加上的 commanderXp(已 floor、負/NaN → 0)。 */
  gained: number;
  /** 升級前等級。 */
  levelBefore: number;
  /** 升級後等級。 */
  levelAfter: number;
  /** 是否升級。 */
  leveledUp: boolean;
  /** 本次升級**新**解鎖了什麼(只列實際新增者)。 */
  unlocked: CommanderUnlocks;
}

/** 套用某個 level 區間 (levelBefore, levelAfter] 觸發的所有解鎖,mutate-in-place。 */
function applyUnlocksForLevelRange(
  p: PlayerProgress,
  levelBefore: number,
  levelAfter: number,
): CommanderUnlocks {
  const unlocked: CommanderUnlocks = { heroes: [], modes: [], cosmetics: [], stages: [], qol: [] };
  if (levelAfter <= levelBefore) return unlocked;

  // 英雄
  for (const [hero, lv] of Object.entries(HERO_UNLOCK_LEVEL) as [Exclude<FactionId, 'celestial'>, number][]) {
    if (lv > levelBefore && lv <= levelAfter) {
      if (unlockHero(p, hero)) unlocked.heroes.push(hero);
    }
  }
  // 模式
  if (!Array.isArray(p.unlockedModes)) p.unlockedModes = [];
  for (const [mode, lv] of Object.entries(MODE_UNLOCK_LEVEL)) {
    if (lv > levelBefore && lv <= levelAfter && !p.unlockedModes.includes(mode)) {
      p.unlockedModes.push(mode);
      unlocked.modes.push(mode);
    }
  }
  // 造型
  if (!Array.isArray(p.unlockedCommanderCosmetics)) p.unlockedCommanderCosmetics = [];
  for (const [cos, lv] of Object.entries(COSMETIC_UNLOCK_LEVEL)) {
    if (lv > levelBefore && lv <= levelAfter && !p.unlockedCommanderCosmetics.includes(cos)) {
      p.unlockedCommanderCosmetics.push(cos);
      unlocked.cosmetics.push(cos);
    }
  }
  // 關卡
  if (!Array.isArray(p.unlockedStages)) p.unlockedStages = [1, 2, 3, 4, 5];
  for (const [stageStr, lv] of Object.entries(STAGE_UNLOCK_LEVEL)) {
    const stage = Number(stageStr);
    if (lv > levelBefore && lv <= levelAfter && !p.unlockedStages.includes(stage)) {
      p.unlockedStages.push(stage);
      unlocked.stages.push(stage);
    }
  }
  // QoL(封頂)
  if (!p.qol || typeof p.qol !== 'object') p.qol = { startingCoins: 0, rerollCharges: 0 };
  if (QOL_STARTING_COINS_AT_LEVEL > levelBefore && QOL_STARTING_COINS_AT_LEVEL <= levelAfter) {
    const next = Math.min((p.qol.startingCoins ?? 0) + QOL_STARTING_COINS_GRANT, QOL_CAPS.startingCoins);
    if (next !== (p.qol.startingCoins ?? 0)) { p.qol.startingCoins = next; unlocked.qol.push('startingCoins'); }
  }
  if (QOL_REROLL_AT_LEVEL > levelBefore && QOL_REROLL_AT_LEVEL <= levelAfter) {
    const next = Math.min((p.qol.rerollCharges ?? 0) + QOL_REROLL_GRANT, QOL_CAPS.rerollCharges);
    if (next !== (p.qol.rerollCharges ?? 0)) { p.qol.rerollCharges = next; unlocked.qol.push('rerollCharges'); }
  }
  return unlocked;
}

/**
 * Mutate-in-place:加 commanderXp、更新 commanderLevel、觸發等級解鎖。
 * 回傳本次解鎖了什麼(供 result-scene UI)。caller 負責 saveProgress(p)。
 *
 * 鐵則:本函式絕不碰 factionXP / freeXP / skillLevels / 傷害 / HP。
 */
export function addCommanderXp(p: PlayerProgress, gained: number): AddCommanderXpResult {
  const safe = Number.isFinite(gained) && gained > 0 ? Math.floor(gained) : 0;
  const xpBefore = Number.isFinite(p.commanderXp as number) ? (p.commanderXp as number) : 0;
  const levelBefore = computeCommanderLevel(xpBefore);
  const xpAfter = xpBefore + safe;
  const levelAfter = computeCommanderLevel(xpAfter);

  p.commanderXp = xpAfter;
  p.commanderLevel = levelAfter;

  const unlocked = applyUnlocksForLevelRange(p, levelBefore, levelAfter);

  return {
    gained: safe,
    levelBefore,
    levelAfter,
    leveledUp: levelAfter > levelBefore,
    unlocked,
  };
}
